import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Anchor, Button, Checkbox, Stack, Text, TextInput, Tooltip} from '@mantine/core';
import React from 'react';

import staticURL from 'lib/staticURL';

import {EMAIL_CREDITS_HINT, PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL} from '../constants';
import SignupStepHeader from '../SignupStepHeader';
import {
  SIGNUP_BUTTON_SIZE,
  SIGNUP_CHECKBOX_SIZE,
  SIGNUP_CONTROL_SIZE,
  SIGNUP_STEP_STACK_SPACING,
  signupControlClassNames,
  useSignupStepStyles,
} from '../signupUi';

interface EmailStepProps {
  email: string;
  error: string | null;
  googleOnly: boolean;
  loading: boolean;
  showGoogleSignup: boolean;
  tosAccepted: boolean;
  onBack: () => void;
  onEmailChange: (value: string) => void;
  onGoogleSignup: () => void;
  onSubmit: () => void;
  onTosAcceptedChange: (value: boolean) => void;
  onUseEmailInstead?: () => void;
}

export default function EmailStep({
  email,
  error,
  googleOnly,
  loading,
  showGoogleSignup,
  tosAccepted,
  onBack,
  onEmailChange,
  onGoogleSignup,
  onSubmit,
  onTosAcceptedChange,
  onUseEmailInstead,
}: EmailStepProps): JSX.Element {
  const {classes} = useSignupStepStyles();
  const controlClassNames = signupControlClassNames(classes);
  const canSubmit = email.trim() !== '' && tosAccepted && !loading;
  const canUseGoogle = tosAccepted && !loading;
  const showGoogleButton = showGoogleSignup || googleOnly;
  const googleButton = (
    <Tooltip
      disabled={tosAccepted}
      label="Accept the Terms of Service and Privacy Policy to continue with Google."
      withArrow={true}
    >
      <span
        {...getTestProps({component: 'EmailStep', element: 'googleTooltipTarget'})}
        style={{display: 'block'}}
      >
        <Button
          disabled={!canUseGoogle}
          fullWidth={true}
          leftIcon={
            <img
              alt=""
              aria-hidden={true}
              height={18}
              src={staticURL('/static/img/model_hub/google_g_logo.svg')}
              width={18}
              {...getTestProps({component: 'EmailStep', element: 'googleLogo'})}
            />
          }
          loading={loading}
          size={SIGNUP_BUTTON_SIZE}
          variant="secondary"
          onClick={onGoogleSignup}
        >
          Continue with Google
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <Stack
      id="model-hub-signup-email-form"
      spacing={SIGNUP_STEP_STACK_SPACING}
      {...getTestProps({component: 'ModelHubSignup', element: 'emailForm'})}
    >
      <SignupStepHeader
        backLabel="Back to home"
        title={googleOnly ? 'Create your account with Google' : 'Create your account'}
        onBack={onBack}
      />
      {error != null && (
        <Alert color="red" role="alert" title="Unable to continue">
          {error}
        </Alert>
      )}
      {!googleOnly && (
        <TextInput
          autoFocus={true}
          classNames={controlClassNames}
          description={EMAIL_CREDITS_HINT}
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          label="Email"
          placeholder="you@company.com"
          size={SIGNUP_CONTROL_SIZE}
          type="email"
          value={email}
          onChange={(event) => {
            onEmailChange(event.currentTarget.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && canSubmit) {
              onSubmit();
            }
          }}
        />
      )}
      <Checkbox
        checked={tosAccepted}
        classNames={{label: classes.checkboxLabel}}
        label={
          <>
            I agree to the{' '}
            <Anchor href={TERMS_OF_SERVICE_URL} rel="noopener noreferrer" target="_blank">
              Benchling.ai Terms of Service
            </Anchor>{' '}
            and acknowledge the{' '}
            <Anchor href={PRIVACY_POLICY_URL} rel="noopener noreferrer" target="_blank">
              Privacy Policy.
            </Anchor>
          </>
        }
        mt={googleOnly ? SIGNUP_STEP_STACK_SPACING : undefined}
        size={SIGNUP_CHECKBOX_SIZE}
        onChange={(event) => {
          onTosAcceptedChange(event.currentTarget.checked);
        }}
      />
      {!googleOnly && (
        <Tooltip
          disabled={tosAccepted}
          label="Accept the Terms of Service and Privacy Policy to continue."
          withArrow={true}
        >
          <span
            {...getTestProps({component: 'EmailStep', element: 'emailTooltipTarget'})}
            style={{display: 'block'}}
          >
            <Button
              disabled={!canSubmit}
              fullWidth={true}
              loading={loading}
              size={SIGNUP_BUTTON_SIZE}
              onClick={onSubmit}
            >
              Continue
            </Button>
          </span>
        </Tooltip>
      )}
      {!googleOnly && showGoogleButton && (
        <Text align="center" color="dimmed" size="sm">
          or
        </Text>
      )}
      {showGoogleButton && googleButton}
      {googleOnly && onUseEmailInstead != null && (
        <Text align="center" size="sm">
          <Anchor component="button" type="button" onClick={onUseEmailInstead}>
            Use email instead
          </Anchor>
        </Text>
      )}
    </Stack>
  );
}
